/**
 * Studio library overview — landing surface for the main canvas when
 * Studio mode is active but no specific kernel or canvas is selected
 * yet. Shows counts and the most-recently-updated items in each
 * section, plus a quick path to dive in.
 *
 * Intentionally lightweight: this is a "where do I want to go" page,
 * not a third major surface. The sidebar already carries the full
 * inventory — this just orients the user when they first land in
 * Studio (or after closing a detail page).
 */
import { NOTE_CLASS, EYEBROW_CLASS } from "../lib/ui.js";
import { cellCount } from "../lib/canvasLayout.js";

function StatCard({ label, value, hint }) {
    return (
        <div className="grid gap-[4px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[14px] py-[12px]">
            <span className={`${EYEBROW_CLASS} text-[color:var(--muted-soft)]`}>{label}</span>
            <span className="text-[24px] leading-[1.05] font-medium text-[color:var(--ink)] tabular-nums">{value}</span>
            {hint ? <span className={`${NOTE_CLASS} text-[10.5px] text-[color:var(--muted-soft)]`}>{hint}</span> : null}
        </div>
    );
}

function RecentList({ heading, items, emptyMessage, getId, getPrimary, getSecondary, onOpen }) {
    return (
        <div className="grid gap-[8px]">
            <span className={`${EYEBROW_CLASS} text-[color:var(--muted-soft)]`}>{heading}</span>
            {items.length === 0 ? (
                <div className="rounded-[2px] border border-dashed border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[12px] py-[10px] text-[11px] text-[color:var(--muted)]">
                    {emptyMessage}
                </div>
            ) : (
                <ul className="grid gap-[4px]">
                    {items.map((it) => (
                        <li key={getId(it)}>
                            <button
                                type="button"
                                onClick={() => onOpen(getId(it))}
                                className="grid w-full [grid-template-columns:minmax(0,1fr)_auto] items-baseline gap-x-[8px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[10px] py-[7px] text-left cursor-pointer hover:border-[color:var(--line-strong)]"
                            >
                                <span className="grid min-w-0 gap-[1px]">
                                    <span className="truncate font-mono text-[11px] leading-[1.18] text-[color:var(--ink)]">
                                        /{getId(it)}
                                    </span>
                                    <span className="truncate text-[10.5px] leading-[1.18] text-[color:var(--muted)]">
                                        {getPrimary(it) || "—"}
                                    </span>
                                </span>
                                <span className="text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted-soft)] tabular-nums">
                                    {getSecondary(it)}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function canvasSummary(canvas) {
    if (canvas?.bodyLoaded === false) return "contents not loaded";
    const count = cellCount(canvas);
    return `${count} kernel${count === 1 ? "" : "s"}`;
}

function formatAgo(ts) {
    if (!ts) return "";
    const diff = Date.now() - ts;
    if (diff < 5000) return "just now";
    if (diff < 60_000) return `${Math.round(diff / 1000)}s ago`;
    if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`;
    return `${Math.round(diff / 86_400_000)}d ago`;
}

export default function StudioOverview({ kernels, canvases, workflows = [], onOpenKernel, onOpenCanvas, onOpenWorkflow }) {
    const sortedKernels = [...kernels].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    const sortedCanvases = [...canvases].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    const sortedWorkflows = [...workflows].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    const recentKernels = sortedKernels.slice(0, 5);
    const recentCanvases = sortedCanvases.slice(0, 5);
    const recentWorkflows = sortedWorkflows.slice(0, 5);
    return (
        <>
            <div className="flex-1 min-h-0 min-w-0 overflow-y-auto">
                <div className="mx-auto grid gap-[24px] py-[24px]">
                    <div className="grid gap-[10px] [grid-template-columns:repeat(3,minmax(0,1fr))] max-[900px]:grid-cols-2 max-[640px]:grid-cols-1">
                        <StatCard
                            label="Kernels"
                            value={String(kernels.length)}
                            hint={kernels.length ? "deterministic recipes" : "save your first kernel from a chat"}
                        />
                        <StatCard
                            label="Canvases"
                            value={String(canvases.length)}
                            hint={canvases.length ? "multi-widget views" : "pin renders, then ↓ canvas in chat"}
                        />
                        <StatCard
                            label="Workflows"
                            value={String(workflows.length)}
                            hint={workflows.length ? "agentic analysis runs" : "create one from the + menu"}
                        />
                    </div>
                    <RecentList
                        heading="Recent kernels"
                        items={recentKernels}
                        emptyMessage="No kernels saved yet. Run a chat and click ↓ kernel on any render to save its recipe."
                        getId={(k) => k.command}
                        getPrimary={(k) => k.title}
                        getSecondary={(k) => formatAgo(k.updatedAt || k.createdAt)}
                        onOpen={onOpenKernel}
                    />
                    <RecentList
                        heading="Recent canvases"
                        items={recentCanvases}
                        emptyMessage="No canvases saved yet. Pin renders in chat, then click ↓ canvas to save them as a bundle."
                        getId={(c) => c.slug}
                        getPrimary={(c) => c.title}
                        getSecondary={(c) => `${canvasSummary(c)} · ${formatAgo(c.updatedAt || c.createdAt)}`}
                        onOpen={onOpenCanvas}
                    />
                    <RecentList
                        heading="Recent workflows"
                        items={recentWorkflows}
                        emptyMessage="No workflows yet. Use the + menu in the Library tab strip to create one."
                        getId={(w) => w.command}
                        getPrimary={(w) => w.title}
                        getSecondary={(w) => formatAgo(w.updatedAt || w.createdAt)}
                        onOpen={onOpenWorkflow}
                    />
                </div>
            </div>
        </>
    );
}
